import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { ContactSyncModel } from '../../../../../types';
import SharedTransferList from '../../../../shared/shared-transfer-list';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';

interface StepFourProps {
  control: Control<Partial<ContactSyncModel>>;
  setValue: UseFormSetValue<Partial<ContactSyncModel>>;
  watch: UseFormWatch<Partial<ContactSyncModel>>;
  errors: FieldErrors<Partial<ContactSyncModel>>;
  domains: string[];
  trigger: UseFormTrigger<Partial<ContactSyncModel>>;
  clearErrors: UseFormClearErrors<Partial<ContactSyncModel>>;
}

export default function StepFour({
  control,
  setValue,
  watch,
  errors,
  domains,
  clearErrors,
  trigger,
}: StepFourProps) {
  const targets = watch('targets')!;
  const targetType = watch('targetType');
  const allSource = watch('allSource');

  function addSelection(emails: string[]) {
    const uniqueEmails = Array.from(new Set([...targets!, ...emails]));
    setValue('targets', uniqueEmails);
    clearErrors();
    if (uniqueEmails.length === 0) {
      trigger('targets');
    }
  }

  function removeSelection(emails: string[]) {
    const newList = targets.filter((userEmail) => !emails.includes(userEmail));
    setValue('targets', newList);
  }

  const handleAddAllChecked = (value: boolean) => {
    setValue('allSource', value);
    if (value) {
      setValue('targetType', targetType);
      setValue('targets', []);
      clearErrors();
    }
  };

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap={1} alignItems="center" sx={{ flexShrink: 0, height: '100%' }}>
        <Stack flexDirection={'row'} gap={4} alignItems={'center'}>
          <Controller
            name={`targetType`}
            control={control}
            render={({ field: props }) => {
              return (
                <FormControl>
                  <FormLabel>
                    {targets.length > 0
                      ? 'Target Type (Only one target type is allowed)'
                      : 'Target Type'}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    defaultValue="User"
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    row
                  >
                    <FormControlLabel
                      value="User"
                      control={<Radio />}
                      label="User"
                      disabled={targets.length > 0 || allSource === true}
                    />
                    <FormControlLabel
                      value="Group"
                      control={<Radio />}
                      label="Group"
                      disabled={targets.length > 0 || allSource === true}
                    />
                    <FormControlLabel
                      value="OrgUnit"
                      control={<Radio />}
                      label="Org Unit"
                      disabled={targets.length > 0 || allSource === true}
                    />
                    <FormControlLabel
                      value="Domain"
                      control={<Radio />}
                      label="Domain"
                      disabled={targets.length > 0 || allSource === true}
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Stack>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
          }}
        >
          <Controller
            rules={{
              required: {
                value: !allSource,
                message: 'Target is required.',
              },
            }}
            name={'targets'}
            control={control}
            render={() => (
              <SharedTransferList
                sources={targets}
                addSelection={addSelection}
                removeSelection={removeSelection}
                handleAddAllChecked={handleAddAllChecked}
                errors={!!errors.targets}
                helperText={errors?.targets?.message || ' '}
                allSource={allSource}
                domains={domains || []}
                sourceType={targetType}
                hasSelectAllOption={true}
              />
            )}
          />
        </Box>
      </Stack>
    </Box>
  );
}
